import React, { useState } from "react";
import {
  Chat,
  X,
  Bug,
  Lightbulb,
  Heart,
  ChatDots,
  Star,
  PaperPlaneRight,
  CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KINDS = [
  { id: "bug", label: "Bug", icon: <Bug size={16} weight="duotone" />, color: "text-red-600 bg-red-50 border-red-200" },
  { id: "idea", label: "Idea", icon: <Lightbulb size={16} weight="duotone" />, color: "text-amber-600 bg-amber-50 border-amber-200" },
  { id: "praise", label: "Praise", icon: <Heart size={16} weight="duotone" />, color: "text-pink-600 bg-pink-50 border-pink-200" },
  { id: "other", label: "Other", icon: <ChatDots size={16} weight="duotone" />, color: "text-slate-600 bg-slate-50 border-slate-200" },
];

export const FeedbackWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("idea");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (message.trim().length < 3) {
      toast.error("Please write a bit more so we can help.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        kind,
        message: message.trim(),
        email: (user?.email || email || "").trim() || null,
        rating: rating || null,
        page: typeof window !== "undefined" ? window.location.pathname : null,
      });
      setSent(true);
      setMessage("");
      setRating(0);
      setTimeout(() => {
        setOpen(false);
        setSent(false);
      }, 1600);
    } catch {
      toast.error("Couldn't send — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Trigger */}
      <button
        data-testid="feedback-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="Send feedback"
        className={`fixed z-40 bottom-5 end-5 md:bottom-6 md:end-6 group ${
          open ? "opacity-0 pointer-events-none" : "opacity-100"
        } transition-opacity`}
      >
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-emerald-500/40 blur-lg group-hover:bg-emerald-500/60 transition-colors"
        />
        <span className="relative flex items-center gap-2 h-11 md:h-12 pe-4 ps-3 rounded-full bg-[#0a192f] text-white border border-emerald-500/40 shadow-[0_10px_30px_rgba(16,185,129,0.25)] hover:border-emerald-400 hover:-translate-y-0.5 transition-all">
          <span className="w-7 h-7 grid place-items-center rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300">
            <Chat size={14} weight="duotone" />
          </span>
          <span className="text-xs md:text-sm font-medium tracking-tight">
            Feedback
          </span>
        </span>
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="feedback-panel"
          className="fixed z-50 bottom-5 end-5 md:bottom-6 md:end-6 w-[calc(100vw-2.5rem)] max-w-sm rounded-2xl bg-white border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.25)] overflow-hidden"
        >
          <div className="bg-[#0a192f] text-white p-4 relative overflow-hidden">
            <div
              aria-hidden
              className="absolute -top-16 -end-16 w-40 h-40 rounded-full blur-3xl opacity-40"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(16,185,129,0.6), transparent 70%)",
              }}
            />
            <div className="relative flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-medium">
                  Feedback
                </p>
                <h4 className="text-base font-semibold tracking-tight mt-1">
                  {sent ? "Thanks! We got it." : "Tell us what's on your mind"}
                </h4>
              </div>
              <button
                data-testid="feedback-close"
                onClick={() => setOpen(false)}
                className="w-8 h-8 grid place-items-center rounded-lg text-slate-300 hover:text-white hover:bg-white/10"
                aria-label="Close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          </div>

          {sent ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 grid place-items-center mx-auto">
                <CheckCircle size={22} weight="fill" className="text-emerald-600" />
              </div>
              <p className="mt-4 text-sm text-slate-700">
                Your feedback helps us ship the right thing next.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4 space-y-3">
              {/* Kind selector */}
              <div className="grid grid-cols-4 gap-1.5">
                {KINDS.map((k) => (
                  <button
                    type="button"
                    key={k.id}
                    data-testid={`feedback-kind-${k.id}`}
                    onClick={() => setKind(k.id)}
                    className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[10px] uppercase tracking-wider font-medium transition-colors ${
                      kind === k.id
                        ? k.color
                        : "border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {k.icon}
                    {k.label}
                  </button>
                ))}
              </div>

              {/* Message */}
              <Textarea
                data-testid="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  kind === "bug"
                    ? "What broke? What did you expect?"
                    : kind === "idea"
                      ? "What would make the calculator smarter?"
                      : "Share anything on your mind…"
                }
                rows={4}
                className="rounded-lg border-slate-300 text-sm resize-none"
                required
                minLength={3}
                maxLength={2000}
              />

              {/* Email (if not signed in) */}
              {!user && (
                <Input
                  data-testid="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional — we'll reply)"
                  className="h-9 rounded-lg border-slate-300 text-sm"
                />
              )}

              {/* Rating */}
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.12em] text-slate-500 font-medium">
                  Rating
                </span>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      data-testid={`feedback-star-${n}`}
                      onClick={() => setRating(n === rating ? 0 : n)}
                      className="p-0.5"
                    >
                      <Star
                        size={18}
                        weight={n <= rating ? "fill" : "regular"}
                        className={n <= rating ? "text-amber-400" : "text-slate-300"}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <Button
                data-testid="feedback-submit"
                type="submit"
                disabled={submitting || message.trim().length < 3}
                className="w-full h-10 rounded-lg btn-neon text-sm"
              >
                {submitting ? (
                  "Sending…"
                ) : (
                  <>
                    <PaperPlaneRight size={14} weight="bold" className="flip-x" />
                    Send feedback
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      )}
    </>
  );
};
